import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Link2,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Target,
  Trash2,
} from 'lucide-react';
import Header from '../components/Header';
import { executionApi } from '../execution/api';
import {
  filterExecutionTree,
  flattenExecution,
  sortObjectives,
  type ExecutionFilters,
  type ExecutionNode,
  type ExecutionObjective,
  type ExecutionRecommendation,
  type ExecutionSnapshot,
  type WorkFilter,
} from '../execution/model';
import ExecutionWorkDialog, {
  ExecutionAlignmentDialog,
  type WorkDialogRequest,
} from '../execution/ExecutionWorkDialog';
import ObjectiveTrackingDialog, { type TrackingTab } from '../execution/ObjectiveTrackingDialog';
import ExecutionCoachDialog from '../execution/ExecutionCoachDialog';
import ExecutionPlan, { executionIcons } from '../execution/ExecutionPlan';
import {
  childKind,
  DeliveryProgress,
  describeError,
  DueDate,
  Health,
  kindLabels,
  Modal,
  OutcomeProgress,
  Owner,
} from '../execution/ExecutionShared';
import '../pages/CompanyWorkPages.css';
import '../execution/ExecutionHub.css';

type DialogState =
  | { kind: 'work'; request: WorkDialogRequest }
  | { kind: 'tracking'; objectiveId: string; tab: TrackingTab }
  | { kind: 'coach'; objectiveId: string; parent?: ExecutionNode }
  | { kind: 'alignment'; node: ExecutionNode }
  | { kind: 'details'; node: ExecutionNode }
  | { kind: 'delete'; node: ExecutionNode }
  | null;
const emptyFilters: ExecutionFilters = {
  query: '',
  work: 'all',
  status: '',
  priority: '',
  ownerId: '',
};

export default function ExecutionHubPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusedId = searchParams.get('objective');
  const [snapshot, setSnapshot] = useState<ExecutionSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filters, setFilters] = useState<ExecutionFilters>({ ...emptyFilters });
  const [view, setView] = useState<'plan' | 'board' | 'map'>('plan');
  const [dialog, setDialog] = useState<DialogState>(null);
  const [starred, setStarred] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const requestId = useRef(0);
  const currentCompany = useRef(companyId);
  currentCompany.current = companyId;
  const load = useCallback(
    async (initial = false) => {
      const request = ++requestId.current;
      if (!companyId) {
        setError('Choose a company from your dashboard to continue.');
        setLoading(false);
        return false;
      }
      if (initial) setLoading(true);
      setError(null);
      try {
        const result = await executionApi.workspace(companyId);
        if (request !== requestId.current || currentCompany.current !== companyId) return false;
        setSnapshot(result);
        return true;
      } catch (err) {
        if (request === requestId.current && currentCompany.current === companyId)
          setError(describeError(err));
        return false;
      } finally {
        if (request === requestId.current && currentCompany.current === companyId)
          setLoading(false);
      }
    },
    [companyId]
  );
  useEffect(() => {
    setSnapshot(null);
    setDialog(null);
    setNotice(null);
    setDeleteError(null);
    setDeleting(false);
    setFilters({ ...emptyFilters });
    setView('plan');
    try {
      setStarred(
        new Set(
          JSON.parse(
            localStorage.getItem(`execution-starred-${companyId}`) ??
              localStorage.getItem(`starredItems_${companyId}`) ??
              '[]'
          )
        )
      );
    } catch {
      setStarred(new Set());
    }
    void load(true);
    return () => {
      requestId.current += 1;
    };
  }, [companyId, load]);
  const focused = snapshot?.objectives.find((objective) => objective.id === focusedId) ?? null;
  const allNodes = useMemo(
    () => (snapshot ? flattenExecution([...snapshot.objectives, ...snapshot.unlinked]) : []),
    [snapshot]
  );
  const filtered = !!(
    filters.query.trim() ||
    filters.work !== 'all' ||
    filters.status ||
    filters.priority ||
    filters.ownerId
  );
  const filteredObjectives = useMemo(
    () =>
      snapshot
        ? (filterExecutionTree(
            snapshot.objectives,
            filters,
            snapshot.currentUserId,
            starred
          ) as ExecutionObjective[])
        : [],
    [snapshot, filters, starred]
  );
  const objectives = sortObjectives(filteredObjectives);
  const visiblePlan =
    focused && snapshot
      ? filterExecutionTree(focused.children, filters, snapshot.currentUserId, starred)
      : [];
  const unlinked = snapshot
    ? filterExecutionTree(snapshot.unlinked, filters, snapshot.currentUserId, starred)
    : [];
  const setFocus = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('objective', id);
    else next.delete('objective');
    setSearchParams(next);
    setDialog(null);
  };
  const star = (node: ExecutionNode) =>
    setStarred((current) => {
      const next = new Set(current);
      if (next.has(node.id)) next.delete(node.id);
      else next.add(node.id);
      try {
        localStorage.setItem(`execution-starred-${companyId}`, JSON.stringify([...next]));
      } catch {
        /* Stars remain available during this session. */
      }
      return next;
    });
  const closeDialog = () => {
    if (snapshot && currentCompany.current === snapshot.company.id) setDialog(null);
  };
  async function saved(message: string) {
    if (!snapshot || currentCompany.current !== snapshot.company.id) return;
    const fresh = await load();
    if (currentCompany.current === snapshot.company.id)
      setNotice(fresh ? message : `${message} Refresh the workspace to see the latest state.`);
  }
  const edit = (node: ExecutionNode) => {
    if (!snapshot?.canEdit) {
      setDialog({ kind: 'details', node });
      return;
    }
    const objective = snapshot.objectives.find(
      (item) => item.id === node.objectiveId || item.id === node.id
    );
    setDialog({
      kind: 'work',
      request: { kind: node.type, node, objectiveTitle: objective?.title },
    });
  };
  const add = (node: ExecutionNode) => {
    const objective = snapshot?.objectives.find(
      (item) => item.id === node.objectiveId || item.id === node.id
    );
    setDialog({
      kind: 'work',
      request: {
        kind: childKind(node.type),
        parent: { type: node.type, id: node.id },
        parentTitle: node.title,
        objectiveTitle: objective?.title,
      },
    });
  };
  const coach = (node: ExecutionNode) => {
    const objective = snapshot?.objectives.find(
      (item) => item.id === node.objectiveId || item.id === node.id
    );
    if (objective) setDialog({ kind: 'coach', objectiveId: objective.id, parent: node });
  };
  const recommendation = (item: ExecutionRecommendation) => {
    if (!snapshot) return;
    const node = allNodes.find(
      (candidate) => candidate.id === item.entityRef.id && candidate.type === item.entityRef.type
    );
    if (!node) {
      setNotice('That item has changed. Refresh the objective to review its latest plan.');
      setDialog(null);
      return;
    }
    if (
      node.type === 'objective' &&
      (item.action === 'record_measure' || item.action === 'clarify_measure')
    ) {
      const objective = snapshot.objectives.find(
        (candidate) => candidate.id === node.objectiveId || candidate.id === node.id
      );
      if (objective)
        setDialog({
          kind: 'tracking',
          objectiveId: objective.id,
          tab: item.action === 'clarify_measure' ? 'measure' : 'checkin',
        });
    } else edit(node);
  };
  async function deleteNode() {
    if (
      !snapshot?.canEdit ||
      dialog?.kind !== 'delete' ||
      deleting ||
      (dialog.node.type !== 'project' && dialog.node.type !== 'task')
    )
      return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await executionApi.deleteNode(snapshot.company.id, dialog.node.type, dialog.node.id);
      await saved(
        `${kindLabels[dialog.node.type]} deleted${dialog.node.children.length ? `, including ${flattenExecution(dialog.node.children).length} supporting task${flattenExecution(dialog.node.children).length === 1 ? '' : 's'}` : ''}.`
      );
      closeDialog();
    } catch (err) {
      if (currentCompany.current === snapshot.company.id) setDeleteError(describeError(err));
    } finally {
      if (currentCompany.current === snapshot.company.id) setDeleting(false);
    }
  }
  const trackingObjective =
    dialog?.kind === 'tracking' || dialog?.kind === 'coach'
      ? snapshot?.objectives.find((item) => item.id === dialog.objectiveId)
      : null;
  if (loading && !snapshot)
    return (
      <div className="execution-empty" style={{ minHeight: '60vh' }}>
        <Loader2 size={27} className="animate-spin" />
        <h1>Loading your objectives and execution plan…</h1>
      </div>
    );
  if (!snapshot)
    return (
      <div className="execution-empty" style={{ minHeight: '60vh' }}>
        <AlertCircle size={30} />
        <h1>Execution is unavailable</h1>
        <p>{error}</p>
        <button type="button" className="execution-button primary" onClick={() => void load(true)}>
          Try again
        </button>
        <Link className="execution-button" to="/dashboard">
          Return to dashboard
        </Link>
      </div>
    );
  return (
    <div className="company-work-page execution-hub">
      <Header
        title="Execution"
        subtitle="Keep objectives in focus. Give every strategy, project and task a clear contribution and owner."
        company={{ name: snapshot.company.name, logo: snapshot.company.logo ?? undefined }}
        actions={
          <div className="execution-actions">
            <Link
              className="execution-button"
              to={`/company/${companyId}/strategy/foundation/objectives`}
            >
              Manage objectives
            </Link>
            <button
              type="button"
              className="execution-button"
              onClick={() => void load()}
              aria-label="Refresh execution workspace"
            >
              <RefreshCw size={14} />
              Refresh
            </button>
          </div>
        }
      />
      <div className="execution-hub-content">
        {error && (
          <div className="execution-notice error" role="alert">
            <span>{error}</span>
            <button type="button" onClick={() => void load()}>
              Retry
            </button>
          </div>
        )}
        {notice && (
          <div className="execution-notice success" role="status">
            <CheckCircle2 size={15} />
            <span>{notice}</span>
            <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss update">
              ×
            </button>
          </div>
        )}
        {!snapshot.canEdit && (
          <div className="execution-notice">
            You have view access to this execution plan. Owners and editors can update the plan and
            record outcome check-ins.
          </div>
        )}
        {focusedId && !focused && (
          <div className="execution-notice">
            <span>This objective is no longer available. Choose another objective below.</span>
            <button type="button" onClick={() => setFocus(null)}>
              Show objectives
            </button>
          </div>
        )}
        {focused ? (
          <>
            <button className="execution-back" type="button" onClick={() => setFocus(null)}>
              <ArrowLeft size={14} />
              All objectives
            </button>
            <section className="execution-objective-focus" aria-label="Focused objective">
              <div className="execution-objective-focus-top">
                <div>
                  <p className="execution-eyebrow">The outcome we are driving towards</p>
                  <h2>{focused.title}</h2>
                  <p className="execution-measure">
                    {focused.primaryMeasure?.label ||
                      focused.measureLabel ||
                      'Define how success will be measured.'}
                  </p>
                  <div className="execution-meta">
                    <Owner owner={focused.owner} />
                    <DueDate value={focused.dueDate} />
                    <Health objective={focused} />
                  </div>
                </div>
                <div className="execution-actions">
                  <button
                    type="button"
                    className="execution-button primary"
                    onClick={() => setDialog({ kind: 'coach', objectiveId: focused.id })}
                  >
                    <Sparkles size={14} />
                    Move this objective forward
                  </button>
                  {snapshot.canEdit && (
                    <button
                      type="button"
                      className="execution-button"
                      onClick={() =>
                        setDialog({
                          kind: 'tracking',
                          objectiveId: focused.id,
                          tab: focused.primaryMeasure ? 'checkin' : 'measure',
                        })
                      }
                    >
                      {focused.primaryMeasure ? 'Update outcome' : 'Set up tracking'}
                    </button>
                  )}
                </div>
              </div>
              <div className="execution-progress-pair">
                <OutcomeProgress objective={focused} detailed />
                <DeliveryProgress value={focused.deliveryCompletion} />
              </div>
              <div className="execution-card-footer">
                <span>
                  {focused.attentionReasons.join(' · ') ||
                    'Keep outcome evidence current and review the contribution of supporting work.'}
                </span>
                <div className="execution-actions">
                  <button
                    type="button"
                    className="execution-button"
                    onClick={() =>
                      setDialog({ kind: 'tracking', objectiveId: focused.id, tab: 'history' })
                    }
                  >
                    Check-in history
                  </button>
                  {snapshot.canEdit && (
                    <>
                      <button
                        type="button"
                        className="execution-button"
                        onClick={() =>
                          setDialog({ kind: 'tracking', objectiveId: focused.id, tab: 'measure' })
                        }
                      >
                        Edit measure
                      </button>
                      <button
                        type="button"
                        className="execution-button"
                        onClick={() => edit(focused)}
                      >
                        Edit objective & owner
                      </button>
                    </>
                  )}
                </div>
              </div>
            </section>
          </>
        ) : (
          <>
            <section className="execution-intro">
              <div>
                <p className="execution-eyebrow">Objectives first</p>
                <h2>Focus on moving the needle.</h2>
                <p>
                  Start with the outcomes you want to achieve. See the strategies, projects and
                  tasks behind each objective, who owns them, and what needs to happen next.
                </p>
              </div>
              <Link
                className="execution-button"
                to={`/company/${companyId}/strategy/foundation/objectives`}
              >
                <Target size={15} />
                {snapshot.objectives.length ? 'Review objectives' : 'Define your objectives'}
              </Link>
            </section>
            <div className="execution-summary" aria-label="Execution summary">
              <span>
                <strong>{snapshot.objectives.length}</strong>objectives
              </span>
              <span>
                <strong>
                  {
                    snapshot.objectives.filter((objective) => objective.attentionReasons.length > 0)
                      .length
                  }
                </strong>
                need attention
              </span>
              <span>
                <strong>{flattenExecution(snapshot.unlinked).length}</strong>work items without an
                objective
              </span>
            </div>
          </>
        )}
        <section className="execution-toolbar" aria-label="Execution filters and views">
          <div className="execution-search">
            <Search size={16} aria-hidden="true" />
            <input
              type="search"
              value={filters.query}
              onChange={(e) => setFilters((current) => ({ ...current, query: e.target.value }))}
              aria-label="Search objectives and supporting work"
              placeholder="Search objectives, work or owners…"
            />
          </div>
          <select
            aria-label="Work focus"
            value={filters.work}
            onChange={(e) =>
              setFilters((current) => ({ ...current, work: e.target.value as WorkFilter }))
            }
          >
            <option value="all">All work</option>
            <option value="my_work">My work</option>
            <option value="needs_attention">Needs attention</option>
            <option value="starred">Starred</option>
          </select>
          <select
            aria-label="Filter by owner"
            value={filters.ownerId}
            onChange={(e) => setFilters((current) => ({ ...current, ownerId: e.target.value }))}
          >
            <option value="">All owners</option>
            {snapshot.people.map((person) => (
              <option value={person.id} key={person.id}>
                {person.name}
              </option>
            ))}
          </select>
          <select
            aria-label="Filter by status"
            value={filters.status}
            onChange={(e) => setFilters((current) => ({ ...current, status: e.target.value }))}
          >
            <option value="">All statuses</option>
            {[...new Set(allNodes.map((node) => node.status))].sort().map((status) => (
              <option key={status} value={status}>
                {status.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
          <select
            aria-label="Filter by priority"
            value={filters.priority}
            onChange={(e) => setFilters((current) => ({ ...current, priority: e.target.value }))}
          >
            <option value="">All priorities</option>
            {['critical', 'high', 'medium', 'low'].map((priority) => (
              <option key={priority} value={priority}>
                {priority}
              </option>
            ))}
          </select>
          {focused && (
            <div className="execution-view-tabs" role="group" aria-label="Supporting work view">
              {(['plan', 'board', 'map'] as const).map((item) => (
                <button
                  type="button"
                  key={item}
                  aria-pressed={view === item}
                  onClick={() => setView(item)}
                >
                  {item === 'plan' ? 'Plan' : item === 'board' ? 'Board' : 'Map'}
                </button>
              ))}
            </div>
          )}
        </section>
        {filtered && (
          <p className="execution-filter-notice">
            Matching work keeps its objective and parent context visible.
            <button type="button" onClick={() => setFilters({ ...emptyFilters })}>
              Clear filters
            </button>
          </p>
        )}
        {focused ? (
          <section className="execution-panel" aria-label="Objective supporting work">
            <div className="execution-panel-heading">
              <div>
                <h2>The plan behind this objective</h2>
                <p>Strategies → Projects → Tasks · Completion here tracks delivery of work.</p>
              </div>
              {snapshot.canEdit && (
                <button type="button" className="execution-button" onClick={() => add(focused)}>
                  <Plus size={14} />
                  Add strategy
                </button>
              )}
            </div>
            {visiblePlan.length === 0 ? (
              <div className="execution-empty">
                <Target size={28} />
                <h3>
                  {filtered
                    ? 'No supporting work matches these filters'
                    : 'Turn this objective into a clear plan'}
                </h3>
                <p>
                  {filtered
                    ? 'Clear the filters to see all strategies, projects and tasks.'
                    : 'Add the strategies that will drive this outcome, then define owned projects and tasks to deliver them.'}
                </p>
                {snapshot.canEdit && !filtered && (
                  <button
                    type="button"
                    className="execution-button primary"
                    onClick={() => coach(focused)}
                  >
                    <Sparkles size={14} />
                    Draft strategies for this objective
                  </button>
                )}
              </div>
            ) : (
              <ExecutionPlan
                key={`${snapshot.company.id}:${focused.id}`}
                nodes={view === 'map' ? [{ ...focused, children: visiblePlan }] : visiblePlan}
                view={view}
                canEdit={snapshot.canEdit}
                starred={starred}
                filtered={filtered}
                onStar={star}
                onEdit={edit}
                onAdd={add}
                onCoach={coach}
              />
            )}
          </section>
        ) : objectives.length > 0 ? (
          <div className="execution-objective-grid" aria-label="Strategic objectives">
            {objectives.map((objective) => {
              const descendants = flattenExecution(objective.children);
              return (
                <article className="execution-objective-card" key={objective.id}>
                  <div className="execution-objective-card-top">
                    <span className="execution-objective-category">
                      <Target size={13} />
                      Objective{objective.referenceNumber ? ` ${objective.referenceNumber}` : ''}
                    </span>
                    <Health objective={objective} />
                  </div>
                  <h3>{objective.title}</h3>
                  <p className="execution-measure">
                    {objective.primaryMeasure?.label ||
                      objective.measureLabel ||
                      'Success measure has not been defined.'}
                  </p>
                  <div className="execution-meta">
                    <Owner owner={objective.owner} />
                    <DueDate value={objective.dueDate} />
                  </div>
                  <div className="execution-progress-pair">
                    <OutcomeProgress objective={objective} />
                    <DeliveryProgress value={objective.deliveryCompletion} />
                  </div>
                  {objective.attentionReasons.length > 0 && (
                    <p className="execution-attention-note">
                      <AlertCircle size={12} />
                      {objective.attentionReasons.slice(0, 2).join(' · ')}
                    </p>
                  )}
                  <div className="execution-card-footer">
                    <span>
                      {descendants.filter((node) => node.type === 'strategy').length} strategies ·{' '}
                      {descendants.filter((node) => node.type === 'project').length} projects ·{' '}
                      {descendants.filter((node) => node.type === 'task').length} tasks
                    </span>
                    <button
                      type="button"
                      className="execution-button"
                      onClick={() => setFocus(objective.id)}
                    >
                      Open objective
                      <ArrowRight size={14} />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <section className="execution-panel execution-empty">
            <Target size={31} />
            <h2>
              {snapshot.objectives.length
                ? 'No objectives match these filters'
                : 'Give your ambition a clear objective'}
            </h2>
            <p>
              {snapshot.objectives.length
                ? 'Try another owner, work focus or search to find the objective and its supporting plan.'
                : 'Define the outcome you want, how success will be measured and when you want to achieve it. You can then build a clear execution plan.'}
            </p>
            {snapshot.objectives.length ? (
              <button
                type="button"
                className="execution-button"
                onClick={() => setFilters({ ...emptyFilters })}
              >
                Clear filters
              </button>
            ) : (
              <Link
                className="execution-button primary"
                to={`/company/${companyId}/strategy/foundation/objectives`}
              >
                Create objectives
              </Link>
            )}
          </section>
        )}
        <section
          className="execution-panel execution-alignment"
          aria-label="Work without an objective"
        >
          <div className="execution-panel-heading">
            <div>
              <h2>Work without an objective</h2>
              <p>
                Link useful work into the plan so its contribution and responsibility are clear.
              </p>
            </div>
            {snapshot.canEdit && (
              <div className="execution-actions">
                <button
                  type="button"
                  className="execution-button"
                  onClick={() =>
                    setDialog({ kind: 'work', request: { kind: 'project', parent: null } })
                  }
                >
                  <Plus size={13} />
                  Unlinked project
                </button>
                <button
                  type="button"
                  className="execution-button"
                  onClick={() =>
                    setDialog({ kind: 'work', request: { kind: 'task', parent: null } })
                  }
                >
                  <Plus size={13} />
                  Unlinked task
                </button>
              </div>
            )}
          </div>
          {unlinked.length === 0 ? (
            <p className="execution-progress-note">
              {snapshot.unlinked.length
                ? 'No unlinked work matches these filters.'
                : 'All existing work is connected to an objective.'}
            </p>
          ) : (
            unlinked.map((node) => {
              const Icon = executionIcons[node.type];
              return (
                <div className="execution-alignment-row" key={node.id}>
                  <span className={`execution-type-icon ${node.type}`}>
                    <Icon size={15} />
                  </span>
                  <div>
                    <strong>{node.title}</strong>
                    <p>
                      {kindLabels[node.type]}
                      {node.children.length
                        ? ` · ${flattenExecution(node.children).length} supporting items`
                        : ''}
                    </p>
                    <Owner owner={node.owner} />
                  </div>
                  <div className="execution-actions">
                    <button type="button" className="execution-button" onClick={() => edit(node)}>
                      {snapshot.canEdit ? 'Edit' : 'Details'}
                    </button>
                    {snapshot.canEdit && (
                      <>
                        <button
                          type="button"
                          className="execution-button"
                          onClick={() => setDialog({ kind: 'alignment', node })}
                        >
                          <Link2 size={13} />
                          Link to objective
                        </button>
                        <button
                          type="button"
                          className="execution-icon-button"
                          aria-label={`Delete ${node.title}`}
                          onClick={() => {
                            setDeleteError(null);
                            setDialog({ kind: 'delete', node });
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </section>
      </div>
      {dialog?.kind === 'work' && (
        <ExecutionWorkDialog
          key={`${snapshot.company.id}:${dialog.request.node?.id ?? `${dialog.request.kind}:${dialog.request.parent?.id ?? 'unlinked'}`}`}
          request={dialog.request}
          snapshot={snapshot}
          onClose={closeDialog}
          onSaved={saved}
          onDelete={(node) => {
            setDeleteError(null);
            setDialog({ kind: 'delete', node });
          }}
        />
      )}
      {dialog?.kind === 'tracking' && trackingObjective && (
        <ObjectiveTrackingDialog
          key={`${snapshot.company.id}:${trackingObjective.id}`}
          objective={trackingObjective}
          companyId={snapshot.company.id}
          canEdit={snapshot.canEdit}
          initialTab={dialog.tab}
          onClose={closeDialog}
          onSaved={saved}
        />
      )}
      {dialog?.kind === 'coach' && trackingObjective && (
        <ExecutionCoachDialog
          key={`${snapshot.company.id}:${trackingObjective.id}`}
          objective={trackingObjective}
          snapshot={snapshot}
          initialParent={dialog.parent}
          onClose={closeDialog}
          onSaved={saved}
          onRecommendation={recommendation}
        />
      )}
      {dialog?.kind === 'alignment' && (
        <ExecutionAlignmentDialog
          key={`${snapshot.company.id}:${dialog.node.id}`}
          node={dialog.node}
          snapshot={snapshot}
          onClose={closeDialog}
          onSaved={saved}
        />
      )}
      {dialog?.kind === 'details' && (
        <Modal
          title={`${kindLabels[dialog.node.type]} details`}
          description={dialog.node.title}
          onClose={closeDialog}
        >
          <p className="execution-dialog-description">
            {dialog.node.description || 'No description added.'}
          </p>
          <Owner owner={dialog.node.owner} />
          <div style={{ margin: '16px 0' }}>
            <DueDate value={dialog.node.dueDate} />
          </div>
          <DeliveryProgress value={dialog.node.deliveryCompletion} />
          {dialog.node.attentionReasons.length > 0 && (
            <div className="execution-notice" style={{ marginTop: 17 }}>
              {dialog.node.attentionReasons.join(' · ')}
            </div>
          )}
          <div className="execution-dialog-footer">
            <button type="button" className="execution-button" onClick={closeDialog}>
              Close
            </button>
          </div>
        </Modal>
      )}
      {dialog?.kind === 'delete' && (
        <Modal
          title={`Delete ${kindLabels[dialog.node.type].toLowerCase()}`}
          description={`Delete “${dialog.node.title}”${dialog.node.children.length ? ` and ${flattenExecution(dialog.node.children).length} supporting task${flattenExecution(dialog.node.children).length === 1 ? '' : 's'}` : ''}? This permanently removes this ${dialog.node.type}${dialog.node.children.length ? ' and all its supporting tasks' : ''}. This cannot be undone.`}
          onClose={closeDialog}
        >
          {dialog.node.children.length > 0 && (
            <div className="execution-delete-confirm">
              <p>These supporting tasks will also be deleted:</p>
              <ul>
                {flattenExecution(dialog.node.children).map((child) => (
                  <li key={child.id}>{child.title}</li>
                ))}
              </ul>
            </div>
          )}
          {deleteError && (
            <div className="execution-notice error" role="alert">
              {deleteError}
            </div>
          )}
          <div className="execution-dialog-footer">
            <button type="button" className="execution-button" onClick={closeDialog}>
              Cancel
            </button>
            <button
              type="button"
              className="execution-button danger"
              onClick={deleteNode}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : `Delete ${dialog.node.type}`}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
